
import { defineComponent } from 'vue';
import { GlobalAlertTypes } from '@momnt-technologies/common-ui/lib/plugins/alerts/types';
import { CardPaymentOption, PaymentProviderCardDetails } from '@/interfaces/payment-options';
import BankAccountSetup from '@/views/BankAccountSetup/BankAccountSetup.vue';
import PageLoader from '@/components/common/PageLoader.vue';
import PaymentSettingsCard from '@/components/common/Cards/PaymentSettingsCard.vue';
import { PaymentProviderBankDetails, VerificationStatuses } from '@/interfaces/bank-account';
import ManagePaymentMethod from '@/components/BankAccountSetup/ManagePaymentMethod.vue';
import CardInput from '@/components/Payments/CardInput.vue';
import { subscribe, unsubscribe, events } from '@/events/eventBus';

export default defineComponent({
  name: 'PaymentSettings',

  components: {
    CardInput,
    BankAccountSetup,
    PageLoader,
    PaymentSettingsCard,
    ManagePaymentMethod,
  },

  data: () => ({
    loading: false,
    expandedBankCard: false,
    expandedCardCard: false,
  }),

  computed: {
    bankAccount(): PaymentProviderBankDetails | null {
      return this.$store.getters['PaymentOptions/getBankAccount'];
    },

    bankAccountVerified(): boolean {
      return this.$store.getters['PaymentOptions/bankAccountIsVerified'];
    },

    card(): PaymentProviderCardDetails | null {
      return this.$store.getters['PaymentOptions/getCard'];
    },
  },

  mounted() {
    subscribe(events.PAYMENT_SETTINGS.EDIT_BANK, this.expandBank);
    subscribe(events.PAYMENT_SETTINGS.EDIT_CARD, this.expandCard);
  },

  methods: {
    expandBank() {
      this.expandedBankCard = true;
      this.expandedCardCard = false;
    },

    expandCard() {
      this.expandedBankCard = false;
      this.expandedCardCard = true;
    },

    async getPaymentOptions() {
      try {
        await this.$store.dispatch('PaymentOptions/getPaymentOptions');
      } catch {
        this.$router.push({ name: 'error' });
      }
    },

    async onCardAddSuccess(card: CardPaymentOption) {
      this.$store.commit('PaymentOptions/setCards', card);

      this.$alerts.add({
        timed: true,
        type: GlobalAlertTypes.SUCCESS,
        text: `You are ready to transact with the debit card ending in ${this.card?.card_last_4}`
      });
    },

    async refreshOptions() {
      this.loading = true;
      await this.getPaymentOptions();
      this.loading = false;
    },

    async onBankSuccess(lastFour: string) {
      await this.refreshOptions();
      this.$alerts.add({
        timed: true,
        type: GlobalAlertTypes.SUCCESS,
        text: `You are ready to transact with the bank account ending in ${lastFour}`
      });
    },
  },

  watch: {
    bankAccount(bankAccount: PaymentProviderBankDetails) {
      if (
        bankAccount
        && [VerificationStatuses.VERIFIED, VerificationStatuses.CRB_ONBOARDED]
          .includes(bankAccount?.bank_status)
      ) {
        this.expandedBankCard = true;
      }
    }
  },

  destroyed() {
    unsubscribe(events.PAYMENT_SETTINGS.EDIT_BANK, this.expandBank);
    unsubscribe(events.PAYMENT_SETTINGS.EDIT_CARD, this.expandCard);
  }
});
